import { useEffect } from 'react';

import { useAuth } from 'context/auth-context';
import useSalesPortalKiloClass from 'hooks/salesPortal/useSalesPortalKiloClass';
import useSalesPortalKiloClassFromUrlParam from 'pages/sales-portal/useKiloClassFromParam';
import { SalesPortalKiloClassRetrieve } from 'types';

import { useCheckout, WhoIsThisFormOption } from './CheckoutContext';

export const KILO_CLASS_NOT_REBOOKABLE_MESSAGE =
  "This class can't be booked again for this athlete: an earlier reservation for it was cancelled late or marked as a no-show. Please pick another class from the calendar.";

export type KiloClassGateTarget = { skip: true } | { skip: false; profileId: string | undefined };

/**
 * Which athlete the gate asks about once Who Is This For is answered. A brand-new profile
 * has no history with any class, and a sub-account only counts once one is actually chosen.
 */
export function resolveKiloClassGateTarget(
  whoIsThisFor: WhoIsThisFormOption,
  profileId: string | undefined,
): KiloClassGateTarget {
  if (whoIsThisFor === 'myself') return { skip: false, profileId: undefined };
  if (whoIsThisFor === 'subAccount') return profileId ? { skip: false, profileId } : { skip: true };
  return { skip: true };
}

export function isBlockedRelationship(
  relationship: SalesPortalKiloClassRetrieve['athleteRelationship'] | undefined,
): boolean {
  return relationship === 'not_rebookable';
}

/**
 * The in-checkout gate for a pre-selected class. The calendar widget hands the class over
 * anonymously, so the earliest point the target athlete is known is after Who Is This For.
 * A class that athlete late-cancelled or no-showed is routed to the existing "This class
 * can't be scheduled" screen (Back to Calendar) before Payment. The server resolves the
 * athlete from the session — own profile or an owned sub-account — never from a
 * client-supplied athlete id.
 */
export default function useKiloClassGate(): void {
  const { form, isStepCompleted, phase, setPhase, setKiloClassErrorMessage, packageData } = useCheckout();
  const { session } = useAuth();
  const { kiloClassId } = useSalesPortalKiloClassFromUrlParam();

  const target = resolveKiloClassGateTarget(form.values.whoIsThisFor, form.values.profileId);
  const enabled =
    phase === 'checkout' &&
    !!kiloClassId &&
    !!session?.user &&
    isStepCompleted('whoIsThisFor') &&
    !target.skip;

  const { data } = useSalesPortalKiloClass({
    gymId: packageData.gym.gymId,
    kiloClassId,
    profileId: target.skip ? undefined : target.profileId,
    sessionUserId: session?.user?.userId,
    enabled,
  });
  const relationship = data?.data.athleteRelationship;

  useEffect(() => {
    if (!enabled || !isBlockedRelationship(relationship)) return;
    setKiloClassErrorMessage(KILO_CLASS_NOT_REBOOKABLE_MESSAGE);
    setPhase('kiloClassError');
  }, [enabled, relationship, setKiloClassErrorMessage, setPhase]);
}
